import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { alertApi } from 'api/cansense/alertApi';

interface SidebarAlertStatus {
  activeAlertCount: number;
  newAlertCount: number;
  acknowledgedCount: number;
  highestSeverity: string | null;
  isConnected: boolean;
  loading: boolean;
  error: boolean;
}

/**
 * Get the WebSocket URL following the same pattern as useDispatchSocket/useCANVMSSocket.
 * In production and through IIS, use same origin (empty string).
 * IIS proxy rules handle forwarding /socket.io/* to the Node.js backend
 * and automatically set the X-Forwarded-Host header for tenant resolution.
 */
const getSocketUrl = (): string => {
  if (process.env.NODE_ENV === 'production') {
    return ''; // Same origin
  }
  // Running through IIS proxy
  if (window.location.port === '8000') {
    return ''; // Same origin through IIS
  }
  // Direct React dev server — connect to Node.js backend
  return 'http://localhost:10000';
};

/**
 * Lightweight hook for sidebar alert status with WebSocket real-time updates.
 * Connects to socket.io for instant alert notifications and uses HTTP polling as fallback.
 * Designed to be safe and non-blocking — failures don't crash the app.
 *
 * @param enabled - Only connect and fetch if true. If false, returns empty state.
 */
export function useSidebarAlertStatus(enabled: boolean = true): SidebarAlertStatus {
  const [status, setStatus] = useState<SidebarAlertStatus>({
    activeAlertCount: 0,
    newAlertCount: 0,
    acknowledgedCount: 0,
    highestSeverity: null,
    isConnected: false,
    loading: enabled,
    error: false,
  });

  const socketRef = useRef<Socket | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const socketConnectedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    if (!enabled) {
      setStatus({
        activeAlertCount: 0,
        newAlertCount: 0,
        acknowledgedCount: 0,
        highestSeverity: null,
        isConnected: false,
        loading: false,
        error: false,
      });
      return;
    }

    const fetchStatus = async () => {
      if (!mountedRef.current) return;

      try {
        const alertStatus = await alertApi.getAlertStatus();
        if (!mountedRef.current) return;

        const { new: newCount, acknowledged: ackCount } = alertStatus.alerts;
        const activeCount = newCount + ackCount;

        setStatus({
          activeAlertCount: activeCount,
          newAlertCount: newCount,
          acknowledgedCount: ackCount,
          highestSeverity: alertStatus.alerts.highestSeverity || null,
          isConnected: true,
          loading: false,
          error: false,
        });
      } catch (error) {
        if (!mountedRef.current) return;
        console.warn('[useSidebarAlertStatus] Failed to fetch alert status:', error);
        setStatus(prev => ({
          ...prev,
          // Still connected if WebSocket is alive, even if HTTP poll failed
          isConnected: socketConnectedRef.current,
          loading: false,
          error: true,
        }));
      }
    };

    // Initial data fetch
    fetchStatus();

    // HTTP polling fallback every 60 seconds
    intervalRef.current = setInterval(fetchStatus, 60000);

    // Connect to WebSocket for real-time alert updates
    // Uses same connection pattern as useDispatchSocket/useCANVMSSocket:
    // - withCredentials for cookie auth
    // - IIS adds X-Forwarded-Host automatically via proxy rules
    // - Connects to default namespace (backend emits alert_update on main io)
    try {
      const socketUrl = getSocketUrl();
      const socket = io(socketUrl, {
        transports: ['websocket', 'polling'],
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 3000,
        reconnectionDelayMax: 30000,
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        if (!mountedRef.current) return;
        socketConnectedRef.current = true;
        setStatus(prev => ({ ...prev, isConnected: true }));
        // Refresh data on (re)connect
        fetchStatus();
      });

      socket.on('disconnect', () => {
        if (!mountedRef.current) return;
        socketConnectedRef.current = false;
        // Only mark disconnected if HTTP polling is also failing
        setStatus(prev => prev.error ? { ...prev, isConnected: false } : prev);
      });

      socket.on('connect_error', () => {
        if (!mountedRef.current) return;
        socketConnectedRef.current = false;
      });

      // Real-time: refresh alert counts when backend emits alert_update
      socket.on('alert_update', () => {
        if (mountedRef.current) {
          fetchStatus();
        }
      });
    } catch (err) {
      console.warn('[useSidebarAlertStatus] Failed to initialize WebSocket:', err);
    }

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      socketConnectedRef.current = false;
    };
  }, [enabled]);

  return status;
}
